// plan-11 S01 / plan-14 S01: viewport + touch + virtual-keyboard reactive snapshot — bible §10, §42

export type ViewportTier = 'narrow' | 'compact' | 'wide'

export interface ViewportSnapshot {
  tier: ViewportTier
  /** legacy field — narrow OR compact = 'narrow', wide = 'wide' */
  size: 'wide' | 'narrow'
  orientation: 'portrait' | 'landscape'
  touch: boolean
  virtualKeyboardOpen: boolean
}

type ChangeCb = (snap: ViewportSnapshot) => void

const listeners = new Set<ChangeCb>()
let snap: ViewportSnapshot

const BP_NARROW = 480
const BP_COMPACT = 720

function tierFor(w: number): ViewportTier {
  if (w < BP_NARROW) return 'narrow'
  if (w < BP_COMPACT) return 'compact'
  return 'wide'
}

function compute(): ViewportSnapshot {
  const w = window.innerWidth
  const h = window.innerHeight
  const tier = tierFor(w)
  const ae = document.activeElement
  const isInput = ae instanceof HTMLInputElement || ae instanceof HTMLTextAreaElement
  // A focused STAGE input (oracle / commune / search / …) must NOT collapse the
  // stage. vt-vk-open hides #vt-stage-mount — if it fired for a stage input, the
  // focused input would be removed from the layout and blur instantly (the mobile
  // "keyboard flashes open then dies" bug). vk-open collapses the stage only for
  // TERMINAL typing, where the focused input lives outside the stage.
  const stageFocused = isInput && ae !== null && !!ae.closest('#vt-stage-mount')
  return {
    tier,
    size: tier === 'wide' ? 'wide' : 'narrow',
    orientation: w >= h ? 'landscape' : 'portrait',
    touch: window.matchMedia('(pointer: coarse)').matches,
    virtualKeyboardOpen:
      !!window.visualViewport &&
      window.visualViewport.height < window.innerHeight - 100 &&
      isInput &&
      !stageFocused,
  }
}

function applyClasses(s: ViewportSnapshot): void {
  const body = document.body
  body.classList.toggle('vt-narrow', s.tier === 'narrow')
  body.classList.toggle('vt-compact', s.tier === 'compact')
  body.classList.toggle('vt-wide', s.tier === 'wide')
  body.classList.toggle('vt-portrait', s.orientation === 'portrait')
  body.classList.toggle('vt-landscape', s.orientation === 'landscape')
  body.classList.toggle('vt-touch', s.touch)
  body.classList.toggle('vt-fine', !s.touch)
  body.classList.toggle('vt-vk-open', s.virtualKeyboardOpen)
  body.classList.toggle('vt-vk-closed', !s.virtualKeyboardOpen)
}

function notify(): void {
  const current = snap
  listeners.forEach((cb) => cb(current))
}

export function initViewport(): void {
  snap = compute()
  applyClasses(snap)

  const recompute = (): void => {
    const next = compute()
    if (
      next.tier !== snap.tier ||
      next.orientation !== snap.orientation ||
      next.touch !== snap.touch ||
      next.virtualKeyboardOpen !== snap.virtualKeyboardOpen
    ) {
      snap = next
      applyClasses(snap)
      notify()
    }
  }

  window.addEventListener('resize', recompute)
  const orientMq = window.matchMedia('(orientation: portrait)')
  orientMq.addEventListener('change', recompute)

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', recompute)
    window.visualViewport.addEventListener('scroll', recompute)
  }
}

export function getViewport(): ViewportSnapshot {
  return snap
}

export function onViewportChange(cb: ChangeCb): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}
